import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mpost, mput, errMsg, EVENT_STATUS, todayISO } from "@/manager/lib";
import { Field, goldBtn } from "@/manager/ui";

export const EVENT_FIELDS = ["title", "date", "start_time", "end_time", "setup_time", "location", "kind", "status", "event_type",
  "client_name", "client_phone", "client_email", "guests", "staff_needed", "services", "external", "finance", "notes"];
export const toPayload = (ev) => Object.fromEntries(EVENT_FIELDS.map((k) => [k, ev[k] ?? (k === "guests" || k === "staff_needed" ? null : ev[k])]));

const BLANK = { title: "", date: todayISO(), start_time: "", end_time: "", setup_time: "", location: "Aura Sant Cugat", kind: "sala",
  status: "provisional", event_type: "", client_name: "", client_phone: "", client_email: "", guests: "", notes: "" };

export default function EventFormDialog({ open, event, onClose, onSaved }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(event ? { ...event, guests: event.guests ?? "" } : BLANK); }, [open, event]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const synced = !!event?.external_source;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...toPayload({ ...event, ...f }), guests: f.guests === "" ? null : Number(f.guests) };
      const saved = event ? await mput(`/events/${event.id}`, payload) : await mpost("/events", payload);
      toast.success(event ? "Evento actualizado" : "Evento creado con sus tareas automáticas");
      onSaved?.(saved);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mgr-event-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{event ? "Editar evento" : "Nuevo evento"}</DialogTitle></DialogHeader>
        {synced && <p className="text-xs bg-[#F7F5F0] border border-[#e6e1d6] rounded-lg p-3 text-[#6b6a66]">Evento sincronizado desde la web: si cambias fecha, título u horario en la web, aquí se actualizará. La ficha operativa no se pierde.</p>}
        <form onSubmit={save} className="space-y-4">
          <Field label="Nombre del evento *"><Input value={f.title} onChange={set("title")} required data-testid="mgr-ev-title" /></Field>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Fecha *"><Input type="date" value={f.date} onChange={set("date")} required data-testid="mgr-ev-date" /></Field>
            <Field label="Montaje"><Input type="time" value={f.setup_time} onChange={set("setup_time")} data-testid="mgr-ev-setup" /></Field>
            <Field label="Inicio"><Input type="time" value={f.start_time} onChange={set("start_time")} data-testid="mgr-ev-start" /></Field>
            <Field label="Fin"><Input type="time" value={f.end_time} onChange={set("end_time")} data-testid="mgr-ev-end" /></Field>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Línea">
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v, location: v === "sala" ? "Aura Sant Cugat" : f.location })}>
                <SelectTrigger data-testid="mgr-ev-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sala">Sala</SelectItem><SelectItem value="externo">Externo</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Estado">
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="mgr-ev-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(EVENT_STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Invitados"><Input type="number" min="0" value={f.guests} onChange={set("guests")} data-testid="mgr-ev-guests" /></Field>
            <Field label="Tipo"><Input value={f.event_type} onChange={set("event_type")} placeholder="Cumpleaños…" data-testid="mgr-ev-type" /></Field>
          </div>
          <Field label="Ubicación"><Input value={f.location} onChange={set("location")} data-testid="mgr-ev-location" /></Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Cliente"><Input value={f.client_name} onChange={set("client_name")} data-testid="mgr-ev-client" /></Field>
            <Field label="Teléfono"><Input value={f.client_phone} onChange={set("client_phone")} data-testid="mgr-ev-phone" /></Field>
            <Field label="Email"><Input value={f.client_email} onChange={set("client_email")} data-testid="mgr-ev-email" /></Field>
          </div>
          <Field label="Observaciones internas"><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="mgr-ev-notes" /></Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={busy} className={goldBtn} data-testid="mgr-ev-save">{event ? "Guardar" : "Crear evento"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
